import React, { useEffect, useState, useMemo } from 'react';
import { 
  Users, Clock, Package, Activity, Gauge, UserCheck, Edit3, Crown, Shield, Star,
  MapPin, X, Check, LayoutGrid, Layers
} from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import { Card, Badge, Button, Modal } from '../components/ui';
import ProductionFloor from '../components/ProductionFloor';
import { formatMinutesToHours } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colorMap = {
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', label: 'text-violet-600', value: 'text-violet-800', placeholder: 'text-violet-400', icon: 'text-violet-500', inputBorder: 'border-violet-300' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', label: 'text-blue-600', value: 'text-blue-800', placeholder: 'text-blue-400', icon: 'text-blue-500', inputBorder: 'border-blue-300' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', label: 'text-amber-600', value: 'text-amber-800', placeholder: 'text-amber-400', icon: 'text-amber-500', inputBorder: 'border-amber-300' },
};

function RoleField({ label, icon: Icon, value, personnel, settingKey, color, onSave }) {
  const [editing, setEditing] = useState(false);
  const c = colorMap[color];
  return (
    <div className={`flex items-center gap-2 ${c.bg} border ${c.border} rounded-lg px-3 py-1.5`} data-testid={`role-${settingKey}`}>
      <Icon className={`w-3.5 h-3.5 ${c.icon}`} />
      <span className={`text-xs font-semibold ${c.label} whitespace-nowrap`}>{label}:</span>
      {editing ? (
        <select value={value} onChange={e => { onSave(settingKey, e.target.value); setEditing(false); }}
          onBlur={() => setEditing(false)}
          className={`border ${c.inputBorder} rounded px-2 py-0.5 text-xs bg-white`} autoFocus>
          <option value="">-- Select --</option>
          {personnel.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
        </select>
      ) : (
        <button onClick={() => setEditing(true)} className={`flex items-center gap-1 text-xs font-medium ${c.value} hover:opacity-70`}>
          {value || <span className={`${c.placeholder} italic`}>Click to set</span>}
          <Edit3 className="w-3 h-3 opacity-40" />
        </button>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, unit, color, active, onClick, children }) {
  const colors = {
    blue: { bg: 'bg-blue-50/80', iconBg: 'bg-blue-100', iconColor: 'text-blue-600', border: 'border-blue-200', activeBg: 'bg-blue-50' },
    emerald: { bg: 'bg-emerald-50/80', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', border: 'border-emerald-200', activeBg: 'bg-emerald-50' },
    orange: { bg: 'bg-orange-50/80', iconBg: 'bg-orange-100', iconColor: 'text-orange-600', border: 'border-orange-200', activeBg: 'bg-orange-50' },
    purple: { bg: 'bg-purple-50/80', iconBg: 'bg-purple-100', iconColor: 'text-purple-600', border: 'border-purple-200', activeBg: 'bg-purple-50' },
    amber: { bg: 'bg-amber-50/80', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', border: 'border-amber-200', activeBg: 'bg-amber-50' },
    teal: { bg: 'bg-teal-50/80', iconBg: 'bg-teal-100', iconColor: 'text-teal-600', border: 'border-teal-200', activeBg: 'bg-teal-50' },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className="relative">
      <button
        onClick={onClick}
        data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
        className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${active ? `${c.activeBg} ${c.border} shadow-sm` : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'} ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-slate-900 font-heading">{value}</span>
              {unit && <span className="text-xs text-slate-500">{unit}</span>}
            </div>
          </div>
          <div className={`p-1.5 rounded ${c.iconBg}`}>
            <Icon className={`w-4 h-4 ${c.iconColor}`} />
          </div>
        </div>
      </button>
      {active && children && (
        <div className={`absolute top-full left-0 right-0 z-30 mt-1 rounded-lg border ${c.border} bg-white shadow-lg max-h-72 overflow-auto animate-fade-in`}>
          {children}
        </div>
      )}
    </div>
  );
}

function TableNameEditor({ table, onSave }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(table.name);
  const handleSave = () => { onSave(table.id, name); setEditing(false); };
  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input value={name} onChange={e => setName(e.target.value)} className="border border-amber-300 rounded px-1.5 py-0.5 text-xs w-28 bg-white" autoFocus onKeyDown={e => e.key === 'Enter' && handleSave()} />
        <button onClick={handleSave} className="text-emerald-600 hover:text-emerald-800"><Check className="w-3 h-3" /></button>
        <button onClick={() => { setName(table.name); setEditing(false); }} className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>
      </div>
    );
  }
  return (
    <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs font-semibold text-slate-800 hover:text-amber-700">
      {table.name} <Edit3 className="w-2.5 h-2.5 opacity-40" />
    </button>
  );
}

export default function Dashboard() {
  const { 
    lines, stations, personnel, projects, shifts, capacity, prepTables,
    fetchAll, renamePrepTable, personnelAssignments,
  } = useAppStore();

  const [roles, setRoles] = useState({ production_manager: '', supervisor: '', senior_team_leader: '' });
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    fetchAll().then(async () => {
      if (useAppStore.getState().lines.length === 0) {
        try { await axios.post(`${API_URL}/api/seed`); } catch(e) {}
        await fetchAll();
      }
    });
    axios.get(`${API_URL}/api/settings`).then(r => setRoles({
      production_manager: r.data.production_manager || '',
      supervisor: r.data.supervisor || '',
      senior_team_leader: r.data.senior_team_leader || '',
    })).catch(() => {});
  }, []);

  const saveRole = (key, value) => {
    setRoles(prev => ({ ...prev, [key]: value }));
    axios.put(`${API_URL}/api/settings`, { [key]: value });
  };

  const toggleCard = (name) => setExpandedCard(prev => prev === name ? null : name);

  // Derived data
  const productionLines = useMemo(() => lines.filter(l => l.line_type !== 'table'), [lines]);
  const tableLines = useMemo(() => lines.filter(l => l.line_type === 'table'), [lines]);
  
  const lineStationCounts = useMemo(() => {
    const counts = {};
    productionLines.forEach(l => { counts[l.id] = stations.filter(s => s.line_id === l.id).length; });
    return counts;
  }, [productionLines, stations]);

  const totalLineStations = useMemo(() => productionLines.reduce((sum, l) => sum + (lineStationCounts[l.id] || 0), 0), [productionLines, lineStationCounts]);

  const assignedStations = useMemo(() => stations.filter(s => s.assigned_personnel_id), [stations]);
  const stationsWithOps = useMemo(() => stations.filter(s => (s.total_time_sec || 0) > 0), [stations]);

  // Assignments breakdown
  const assignmentsList = useMemo(() => {
    if (!personnelAssignments) return [];
    return personnel.map(p => {
      const a = personnelAssignments[p.id];
      if (!a) return { ...p, location: null };
      if (a.type === 'line') return { ...p, location: `${a.line} - St.${a.stations?.join(', St.')}`, locationType: 'line' };
      if (a.type === 'table') return { ...p, location: a.table, locationType: 'table' };
      return { ...p, location: null };
    });
  }, [personnel, personnelAssignments]);

  const assignedCount = assignmentsList.filter(a => a.location).length;
  const unassignedCount = personnel.length - assignedCount;

  // Active prep table personnel count  
  const tablePersonnelCount = useMemo(() => {
    return prepTables.reduce((sum, t) => sum + (t.assigned_personnel || []).length + (t.assigned_personnel_id && !(t.assigned_personnel || []).length ? 1 : 0), 0);
  }, [prepTables]);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Production Dashboard</h1>
          <p className="text-slate-500 mt-0.5 text-sm">Line balancing, simulation & production planning</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="info" className="text-sm">
            <Activity className="w-4 h-4 mr-1" />
            Production Assembly Floor
          </Badge>
        </div>
      </div>

      {/* Management Roles */}
      <div className="flex flex-wrap items-center gap-2" data-testid="management-roles">
        <RoleField label="Production Manager" icon={Crown} value={roles.production_manager} personnel={personnel} settingKey="production_manager" color="violet" onSave={saveRole} />
        <RoleField label="Supervisor" icon={Shield} value={roles.supervisor} personnel={personnel} settingKey="supervisor" color="blue" onSave={saveRole} />
        <RoleField label="Senior Team Leader" icon={Star} value={roles.senior_team_leader} personnel={personnel} settingKey="senior_team_leader" color="amber" onSave={saveRole} />
      </div>

      {/* KPI Cards - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Production Lines */}
        <StatCard icon={Layers} label="Production Lines" value={productionLines.length} unit={`${totalLineStations} st`} color="blue"
          active={expandedCard === 'lines'} onClick={() => toggleCard('lines')}>
          <div className="p-3 space-y-2">
            <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Line Details</p>
            {productionLines.map(l => (
              <div key={l.id} className="flex items-center justify-between py-1.5 px-2 bg-slate-50 rounded text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-5 flex items-center justify-center bg-blue-600 text-white rounded text-[10px] font-bold">{l.code}</span>
                  <span className="font-medium text-slate-800">{l.name}</span>
                </div>
                <span className="text-slate-500 font-mono">{lineStationCounts[l.id] || 0} st</span>
              </div>
            ))}
            <div className="border-t border-slate-100 pt-1.5 flex justify-between text-[10px] text-slate-500 px-2">
              <span>Total</span>
              <span className="font-semibold">{totalLineStations} stations</span>
            </div>
          </div>
        </StatCard>

        {/* Production Tables */}
        <StatCard icon={LayoutGrid} label="Prep Tables" value={prepTables.length} unit="tables" color="amber"
          active={expandedCard === 'tables'} onClick={() => toggleCard('tables')}>
          <div className="p-3 space-y-2">
            <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Table Details (click name to rename)</p>
            {prepTables.map(t => (
              <div key={t.id} className="flex items-center justify-between py-1.5 px-2 bg-slate-50 rounded text-xs">
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-5 flex items-center justify-center rounded text-[10px] font-bold ${t.is_active ? 'bg-amber-500 text-white' : 'bg-slate-300 text-white'}`}>PT{t.table_number}</span>
                  <TableNameEditor table={t} onSave={(id, name) => renamePrepTable(id, name)} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">{t.operations?.length || 0} ops</span>
                  {!t.is_active && <Badge variant="default" className="text-[9px] py-0">OFF</Badge>}
                </div>
              </div>
            ))}
          </div>
        </StatCard>

        {/* Personnel */}
        <StatCard icon={Users} label="Personnel" value={personnel.length} unit="workers" color="orange"
          active={expandedCard === 'personnel'} onClick={() => toggleCard('personnel')}>
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold text-slate-500 uppercase">All Personnel</p>
              <div className="flex gap-2 text-[10px]">
                <span className="text-emerald-600 font-medium">{assignedCount} assigned</span>
                <span className="text-slate-400">{unassignedCount} free</span>
              </div>
            </div>
            <div className="space-y-1 max-h-52 overflow-y-auto">
              {assignmentsList.sort((a, b) => (b.location ? 1 : 0) - (a.location ? 1 : 0)).map(p => (
                <div key={p.id} className={`flex items-center justify-between py-1 px-2 rounded text-xs ${p.location ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                  <span className="font-medium text-slate-800">{p.name}</span>
                  {p.location ? (
                    <span className={`text-[10px] font-mono ${p.locationType === 'table' ? 'text-amber-600' : 'text-blue-600'}`}>
                      <MapPin className="w-2.5 h-2.5 inline mr-0.5" />{p.location}
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400 italic">Unassigned</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </StatCard>

        {/* Assigned & Operations */}
        <StatCard icon={UserCheck} label="Assigned" value={assignedCount} unit={`/ ${personnel.length}`} color="purple"
          active={expandedCard === 'assigned'} onClick={() => toggleCard('assigned')}>
          <div className="p-3">
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="text-center p-1.5 bg-blue-50 rounded">
                <p className="text-lg font-bold text-blue-800">{assignedStations.length}</p>
                <p className="text-[9px] text-blue-600">Stations w/ Personnel</p>
              </div>
              <div className="text-center p-1.5 bg-teal-50 rounded">
                <p className="text-lg font-bold text-teal-800">{stationsWithOps.length}</p>
                <p className="text-[9px] text-teal-600">Stations w/ Operations</p>
              </div>
            </div>
            <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Active Assignments</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {assignmentsList.filter(a => a.location).map(p => (
                <div key={p.id} className="flex items-center justify-between py-1 px-2 bg-purple-50 rounded text-xs">
                  <span className="font-medium text-slate-800">{p.name}</span>
                  <span className="text-[10px] font-mono text-purple-600">{p.location}</span>
                </div>
              ))}
              {tablePersonnelCount > 0 && (
                <div className="text-[10px] text-amber-600 px-2 pt-1">+ {tablePersonnelCount} at prep tables</div>
              )}
            </div>
          </div>
        </StatCard>

        {/* Projects */}
        <StatCard icon={Package} label="Projects" value={projects.length} unit="loaded" color="teal"
          active={expandedCard === 'projects'} onClick={() => toggleCard('projects')}>
          <div className="p-3 space-y-1">
            <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Project List</p>
            {projects.length === 0 && <p className="text-xs text-slate-400 italic px-2">No projects loaded</p>}
            {projects.map(pr => {
              const line = lines.find(l => l.id === pr.line_id);
              return (
                <div key={pr.id} className="flex items-center justify-between py-1.5 px-2 bg-slate-50 rounded text-xs">
                  <div>
                    <span className="font-medium text-slate-800">{pr.name}</span>
                    {line && <span className="ml-1.5 text-[10px] text-blue-500 font-mono">{line.code}</span>}
                  </div>
                  <span className="text-[10px] text-slate-500">{pr.operations_count || 0} ops</span>
                </div>
              );
            })}
          </div>
        </StatCard>

        {/* Active Capacity */}
        <StatCard icon={Gauge} label="Active Capacity" 
          value={capacity ? formatMinutesToHours(capacity.daily_minutes * assignedCount) : '--'} 
          unit="/ day" color="emerald">
          <div />
        </StatCard>
      </div>

      {/* Click outside to close expanded card */}
      {expandedCard && <div className="fixed inset-0 z-20" onClick={() => setExpandedCard(null)} />}

      {/* Production Capacity Section */}
      {capacity && (
        <Card className="!p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-heading font-semibold text-slate-900">Production Capacity</h2>
            <div className="flex items-center gap-3">
              {shifts[0] && (
                <div className="flex items-center gap-2 text-[10px]">
                  <Badge variant="info">{shifts[0].name}: {shifts[0].start_time} - {shifts[0].end_time}</Badge>
                  {shifts[0].gross_minutes > 0 && (
                    <span className="text-slate-500">
                      Gross {shifts[0].gross_minutes}m
                      {shifts[0].line_setup_min > 0 && ` - Setup ${shifts[0].line_setup_min}m`}
                      {shifts[0].line_cleanup_min > 0 && ` - Cleanup ${shifts[0].line_cleanup_min}m`}
                      {(shifts[0].breaks || []).length > 0 && ` - Breaks ${(shifts[0].breaks || []).reduce((s,b) => s + (b.duration_min || 0), 0)}m`}
                      {' '}= Net {shifts[0].net_work_minutes}m
                    </span>
                  )}
                  {!shifts[0].gross_minutes && shifts[0].net_work_minutes > 0 && (
                    <span className="text-slate-500">Net: {shifts[0].net_work_minutes}m ({Math.round(shifts[0].net_work_minutes/60*10)/10}h)</span>
                  )}
                </div>
              )}
              <Clock className="w-4 h-4 text-slate-400" />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            {/* Per Worker */}
            <div className="space-y-2">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Per Worker</p>
              {[['daily_minutes','Daily','bg-slate-50 border-slate-200'],['weekly_minutes','Weekly','bg-slate-50 border-slate-200'],['monthly_minutes','Monthly','bg-slate-50 border-slate-200']].map(([k,l,cls]) => (
                <div key={k} className={`p-2.5 rounded border ${cls} h-[76px] flex flex-col justify-between`}>
                  <div className="flex items-baseline justify-between">
                    <span className="text-lg font-bold text-slate-900">{formatMinutesToHours(capacity[k])}</span>
                    <span className="text-[10px] text-slate-400">{l}</span>
                  </div>
                  <p className="text-[10px] text-blue-600 font-mono">{capacity[k]} dk</p>
                </div>
              ))}
            </div>

            {/* Total Personnel */}
            <div className="space-y-2">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Total ({personnel.length} workers)</p>
              {[['daily_minutes','Daily'],['weekly_minutes','Weekly'],['monthly_minutes','Monthly']].map(([k,l]) => (
                <div key={k} className="p-2.5 rounded border border-blue-200 bg-blue-50/60 h-[76px] flex flex-col justify-between">
                  <div className="flex items-baseline justify-between">
                    <span className="text-lg font-bold text-blue-800">{formatMinutesToHours(capacity[k] * personnel.length)}</span>
                    <span className="text-[10px] text-blue-400">{l}</span>
                  </div>
                  <p className="text-[10px] text-blue-600 font-mono">{capacity[k] * personnel.length} dk</p>
                </div>
              ))}
            </div>

            {/* Active Assigned */}
            <div className="space-y-2">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Active ({assignedCount} assigned)</p>
              {[['daily_minutes','Daily'],['weekly_minutes','Weekly'],['monthly_minutes','Monthly']].map(([k,l]) => {
                const total = capacity[k] * personnel.length;
                const active = capacity[k] * assignedCount;
                const pct = total > 0 ? Math.round((active / total) * 100) : 0;
                return (
                  <div key={k} className="p-2.5 rounded border border-emerald-200 bg-emerald-50/60 h-[76px] flex flex-col justify-between">
                    <div className="flex items-baseline justify-between">
                      <span className="text-lg font-bold text-emerald-800">{formatMinutesToHours(active)}</span>
                      <span className="text-[10px] text-emerald-500">{l}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-emerald-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[9px] text-emerald-600 font-semibold whitespace-nowrap">{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Production Floor */}
      <Card className="!p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-slate-900">Production Assembly Floor</h2>
          <div className="flex items-center gap-2">
            {shifts[0] && <Badge variant="info">Shift: {shifts[0].start_time} - {shifts[0].end_time}</Badge>}
          </div>
        </div>
        <ProductionFloor />
      </Card>
    </div>
  );
}
